/** Same-origin tabs only; used when expert→company URL handoff is handled on one tab. */
export const SLASHEXPERTS_COMPANY_APP_SWITCH_CHANNEL =
  "slashexperts_company_app_switch";

const COMPANY_CROSS_APP_SWITCH_BROADCAST_PENDING_KEY =
  "slashexperts_v1_company_cross_app_switch_broadcast_pending";

/** Per-tab: matches `sourceBroadcastId` on outgoing messages so this tab can skip its own reset. */
const APP_SWITCH_BROADCAST_SOURCE_ID_KEY =
  "slashexperts_v1_app_switch_broadcast_source_id";

/**
 * If this tab posted `app_switch_reset` with this id, it should not navigate to root.
 * Call from the BroadcastChannel listener before assigning `location`.
 */
export function consumeAppSwitchBroadcastIfFromThisTab(
  sourceBroadcastId: string | undefined
): boolean {
  if (!sourceBroadcastId) return false;
  try {
    if (
      sessionStorage.getItem(APP_SWITCH_BROADCAST_SOURCE_ID_KEY) !==
      sourceBroadcastId
    ) {
      return false;
    }
    sessionStorage.removeItem(APP_SWITCH_BROADCAST_SOURCE_ID_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Set before `signinRedirect` during expert→company handoff (HomeView). */
export function markCompanyCrossAppSwitchSiblingBroadcastPending(): void {
  try {
    sessionStorage.setItem(COMPANY_CROSS_APP_SWITCH_BROADCAST_PENDING_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function clearCompanyCrossAppSwitchSiblingBroadcastPending(): void {
  try {
    sessionStorage.removeItem(COMPANY_CROSS_APP_SWITCH_BROADCAST_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/** After successful Cognito callback + Rails sign-in when a cross-app switch was in progress. */
export function broadcastCompanyCrossAppSwitchToSiblingsIfPending(): void {
  try {
    if (
      sessionStorage.getItem(COMPANY_CROSS_APP_SWITCH_BROADCAST_PENDING_KEY) !==
      "1"
    ) {
      return;
    }
    sessionStorage.removeItem(COMPANY_CROSS_APP_SWITCH_BROADCAST_PENDING_KEY);
  } catch {
    return;
  }
  broadcastAppSwitchToSiblingTabs();
}

/**
 * Cognito callback success: notify sibling tabs once — either for expert→company handoff
 * (pending flag) or for any other successful sign-in, but not both (avoids double broadcast
 * overwriting sessionStorage used to skip reload on the originating tab).
 */
export function broadcastAppSwitchAfterCompanyCallbackSuccess(): void {
  let wasCrossAppHandoffPending = false;
  try {
    wasCrossAppHandoffPending =
      sessionStorage.getItem(COMPANY_CROSS_APP_SWITCH_BROADCAST_PENDING_KEY) ===
      "1";
  } catch {
    /* ignore */
  }
  broadcastCompanyCrossAppSwitchToSiblingsIfPending();
  if (!wasCrossAppHandoffPending) {
    broadcastAppSwitchToSiblingTabs();
  }
}

export function broadcastAppSwitchToSiblingTabs(): void {
  let sourceBroadcastId: string;
  try {
    sourceBroadcastId = crypto.randomUUID();
  } catch {
    sourceBroadcastId = `${Date.now()}-${Math.random()}`;
  }
  try {
    sessionStorage.setItem(
      APP_SWITCH_BROADCAST_SOURCE_ID_KEY,
      sourceBroadcastId
    );
  } catch {
    /* sessionStorage unavailable — still notify siblings; origin tab may not self-skip */
  }
  try {
    const ch = new BroadcastChannel(SLASHEXPERTS_COMPANY_APP_SWITCH_CHANNEL);
    ch.postMessage({
      type: "app_switch_reset",
      sourceBroadcastId,
    });
    /* Do not ch.close() immediately — same pattern as cognito_company_logout. */
  } catch {
    try {
      sessionStorage.removeItem(APP_SWITCH_BROADCAST_SOURCE_ID_KEY);
    } catch {
      /* ignore */
    }
  }
}
